import includes from 'lodash/includes';
import React from 'react';
import {render as domRender} from 'react-dom';
import cloudinary from 'cloudinary-core';
import {window} from 'ssr-window';
import APIUtils, {APIRegistry} from '@spothero/utils/api';
import DOMUtils from '@spothero/utils/dom';
import SentryUtils from '@spothero/utils/sentry';
import SearchAPI from 'api/search';
import UserAPI from 'api/user';
import Config from 'config';
import SearchRequestUtils from 'search-request/search-request-utils';
import UserUtils from 'user/user-utils';
import BranchUtils from 'utils/branch';
import ExperimentUtils from 'utils/experiment';
import SegmentUtils from 'utils/segment';
import GTMUtils from 'utils/gtm';
import StripeUtils from 'utils/stripe';
import SHLoader from 'common/loader/SHLoader';

const NUM_RETRIES = 3;
const container = document.querySelector('.root');

const loadData = async render => {
    const {
        location: {
            pathname,
            search
        }
    } = window;
    const [
        userStatus,
        searchParams,
    ] = await Promise.all([
        UserAPI.getLoginStatus(),
        SearchAPI.cleanParams({
            url: `${pathname}${search}`
        }),
    ]);
    const {
        data: {
            data: {
                logged_in: loggedIn
            }
        }
    } = userStatus;
    const {
        data: {
            data: cleanedParams
        }
    } = searchParams;
    const {
        page_info: {
            page_type: pageType,
            setup,
        }
    } = cleanedParams;
    const setupData = await SearchRequestUtils.loadDataSequence({
        pageType,
        setup,
        citySlug: setup.city.slug,
        isNewCity: true
    });
    let userData;

    if (setupData.willRedirect) {
        // if an error happened on initial load in the sequence of API calls, the page will redirect and we don't want to momentarily move forward until that happens
        // otherwise there will be more errors in the console before the redirect kicks in quick enough
        return;
    }

    if (loggedIn) {
        const {
            data: {
                data
            }
        } = await UserAPI.getData();

        userData = UserUtils.normalize({data});
    }

    render({
        userData,
        cleanedParams,
        setupData
    });
};

const initUtils = ({render, isMobile = false}) => {
    if (Config.isSegmentEnabled) {
        SegmentUtils.init({
            apiKey: (Config.isDeployed) ? Config.segmentApiKey : 'debug',
            debug: !Config.isDeployed,
        });
    }

    if (Config.isDeployed) {
        SentryUtils.init({
            apiKey: Config.sentryApiPublicKey,
            isAuthenticated: false,
            release: process.env.SPA_BUILD_TAG,
            additionalRavenOptions: {
                environment: (Config.isProduction) ? 'production' : 'staging'
            }
        });
    }

    if (Config.branchIOApiKey) {
        BranchUtils.init(Config.branchIOApiKey);
    }

    StripeUtils.init(Config.stripePublicApiKey);

    ExperimentUtils.init(() => {
        loadData(render);
    });

    /* eslint-disable no-undefined */
    GTMUtils.push({
        client: (isMobile) ? 'mobile' : 'desktop',
        dateInt: `${new Date().getTime()}`,
        facebookLogoutUrl: '/logout',
        userId: undefined, // eslint-disable-line no-undefined
        facebookOAuth: undefined, // eslint-disable-line no-undefined
        facebookAppId: Config.facebookAppId,
        facebookChannelUrl: Config.facebookChannelUrl,
    });
    /* eslint-enable no-undefined */
};

const init = async ({render, isMobile}) => {
    if (isMobile) {
        DOMUtils.addClass('html', 'mobile');
    }

    Config.set({
        spotHeroAPIUrl: process.env.SPOTHERO_API_URL,
        staticFilesDomain: process.env.STATIC_FILES_DOMAIN,
        siteUrl: process.env.SITE_URL,
        serviceAPIUrl: `https://services.${(process.env.IS_PRODUCTION) ? 'production' : 'staging'}.spothero.com`,
        isDeployed: (process.env.IS_DEPLOYED) ? JSON.parse(process.env.IS_DEPLOYED) : false,
        isProduction: (process.env.IS_PRODUCTION) ? JSON.parse(process.env.IS_PRODUCTION) : false,
        staticBaseUrl: document.getElementById('asset-loader').src.split('js')[0],
        isPrerender: includes(window.navigator.userAgent, 'Prerender')
    });

    APIUtils.init({
        baseUrl: Config.spotHeroAPIUrl,
        additionalHeaders: {
            Authorization: null,
            'Content-Type': 'application/x-www-form-urlencoded'
        }
    });

    APIRegistry.create({
        name: 'services',
        config: {
            baseUrl: Config.serviceAPIUrl
        }
    });

    const searchAPIUtils = APIRegistry.create({
        name: 'ratesSearch',
        config: {
            baseUrl: Config.spotHeroAPIUrl
        }
    });

    searchAPIUtils.addRetry({
        retries: 10
    });

    if (!container.innerHTML) {
        domRender(
            <SHLoader />,
            container
        );
    }

    for (let i = 0; i < NUM_RETRIES; i++) {
        try {
            const {
                data: {
                    data
                }
            } = await APIUtils.get('consumer-web-config/'); // eslint-disable-line no-await-in-loop

            data.cloudinary = cloudinary.Cloudinary.new({
                cloud_name: data.cloudinaryCloudName || 'spothero' // eslint-disable-line camelcase
            });

            Config.set(data);
            initUtils({render, isMobile});

            break;
        } catch (error) {
            console.log(error); // eslint-disable-line no-console
        }
    }
};

export default {
    init
};
